import { PortalContainerProvider, SkyDropdown, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import { US_TIMEZONE_IDS } from "@shared/types/schedules";
import { formerZoneNames } from "@shared/time/canonicalZone";
import { useDisplayTzChoice, useSaveDisplayTz } from "@/hooks/use-display-tz";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import {
  browserTimeZone,
  currentZoneName,
  zoneIdLine,
  zoneLabel,
  zoneOptionLabel,
} from "@/lib/time/displayTime";

/**
 * The user menu's "Time zone" (round 3, 2026-09-30). The owner: "we should store the user
 * timezone and display the values aligned to it"; then (2026-09-30, binding): "show in local time
 * always ... the user profile can let them overwrite; in that case we can show all timezones".
 * One zone per person across every tenant and instance (`PUT /me/preferences`); every instant on
 * screen renders in it (`useDisplayTz`).
 *
 * The row says where the times come from: "India (IST), this device" while no zone is chosen
 * (the device's zone, read on every load, so it follows a person who travels), or
 * "Pacific (PDT), chosen". The searchable `SkyDropdown` offers, in groups:
 * - "Use this device's time zone (India (IST))", which stores null;
 * - the 7 US zones the scheduler offers (`US_TIMEZONE_IDS`), in its order;
 * - every other IANA zone the browser knows, by its current name, sorted.
 * Each zone option is labelled the way the schedule picker labels a zone ("India (IST) ·
 * 11:42 AM") over its current IANA name ("Asia/Kolkata"). Choosing one saves it at once (a notice
 * says so); a refused save stays beside the picker.
 *
 * The list portals into the picker, as `TenantInstancePicker`'s do: inside the menu's
 * `SkyPopover` (layer 1060) a list portalled to the body (layer 500) would open behind it.
 */

const DEVICE_OPTION = "device";

/**
 * Every zone this engine names, by its current IANA name and once each, sorted, with `UTC`, this
 * device's and the stored one included. ICU lists the old names (`Asia/Calcutta`, `Europe/Kiev`);
 * `currentZoneName` offers the one a person searches for and the server stores.
 */
function allZones(device: string, stored: string): string[] {
  let zones: string[];
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = [];
  }
  return [...new Set([...zones, "UTC", device, stored].map(currentZoneName))].sort((a, b) =>
    a.localeCompare(b),
  );
}

/**
 * What the search matches. `SkyDropdown` searches an option's `text` only (no filter prop in
 * 2.17.0), so the text carries the label, the IANA name with its city in words and any old name:
 * "Kolkata", "Asia", "Asia/Kol", "Los Angeles" and "Calcutta" each find their zone.
 */
function searchText(zone: string, label: string): string {
  const former = formerZoneNames(zone);
  const parts = [label, zoneIdLine(zone)];
  if (former.length > 0) parts.push(`formerly ${former.join(", ")}`);
  return parts.join(", ");
}

export function DisplayTimezonePicker({ className }: { className?: string }) {
  const { zone, followsDevice } = useDisplayTzChoice();
  const save = useSaveDisplayTz();
  const notice = useNotice();
  const failure = useInlineError();
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);
  const device = browserTimeZone();

  // Labels carry the time there now: built once per render of the menu, not per keystroke.
  const { groups, labels, deviceText } = useMemo(() => {
    const now = new Date();
    const labels = new Map<string, string>();
    const option = (value: string) => {
      const label = zoneOptionLabel(value, now);
      labels.set(value, label);
      return {
        value,
        text: searchText(value, label),
        selectedText: `${zoneLabel(value, now)}, chosen`,
      };
    };
    const us = new Set(US_TIMEZONE_IDS);
    const deviceText = `Use this device's time zone (${zoneLabel(device, now)})`;
    return {
      labels,
      deviceText,
      groups: [
        {
          label: "This device",
          options: [
            {
              value: DEVICE_OPTION,
              text: searchText(device, deviceText),
              selectedText: `${zoneLabel(device, now)}, this device`,
            },
          ],
        },
        { label: "United States", options: US_TIMEZONE_IDS.map(option) },
        {
          label: "Rest of the world",
          options: allZones(device, zone)
            .filter((value) => !us.has(value))
            .map(option),
        },
      ],
    };
  }, [device, zone]);

  const selected = followsDevice ? DEVICE_OPTION : zone;

  function choose(value: string | undefined) {
    if (!value || value === selected) return;
    const timezone = value === DEVICE_OPTION ? null : value;
    failure.clear();
    save.mutate(
      { timezone },
      {
        onSuccess: (view) =>
          notice.success(
            view.timezone === null
              ? `Times now follow this device: ${zoneLabel(browserTimeZone())}.`
              : `Times now show in ${zoneLabel(view.timezone)}.`,
          ),
        onError: (error) => failure.show(error, "The time zone could not be saved."),
      },
    );
  }

  return (
    <div className={className} data-testid="display-timezone">
      <PortalContainerProvider value={portal}>
        <SkyDropdown
          label="Time zone"
          testId="display-timezone-dropdown"
          options={groups}
          selectedOptions={[selected]}
          disabled={save.isPending}
          listboxMaxHeight="320px"
          listboxMaxWidth="288px"
          onOptionSelect={(values) => choose(values[0])}
          optionTemplate={(option) =>
            option.value === DEVICE_OPTION ? (
              // It wraps rather than cut the device's zone off in a narrow menu.
              <span data-testid={`display-timezone-option-${option.value}`}>
                <span className="block whitespace-normal">{deviceText}</span>{" "}
                <SkyText size={200} color="muted" block truncate title={zoneIdLine(device)}>
                  {zoneIdLine(device)}
                </SkyText>
              </span>
            ) : (
              // The label, and under it the IANA name the search also matches, so a reader who
              // typed "Kolkata" sees why India is listed.
              <span data-testid={`display-timezone-option-${option.value}`}>
                <span className="block truncate">{labels.get(option.value)}</span>{" "}
                <SkyText size={200} color="muted" block truncate title={zoneIdLine(option.value)}>
                  {zoneIdLine(option.value)}
                </SkyText>
              </span>
            )
          }
        />
      </PortalContainerProvider>
      <div ref={setPortal} />
      {failure.error ? (
        <div className="mt-2">
          <InlineErrorBar
            error={failure.error}
            title="The time zone could not be saved"
            onDismiss={failure.clear}
            testId="display-timezone-error"
          />
        </div>
      ) : null}
      <SkyText size={200} color="muted" block className="mt-1">
        Every time on these screens is shown in this zone. Emails to you use the zone you choose
        here, or Pacific time while it follows this device. Teams messages use Pacific time.
      </SkyText>
    </div>
  );
}
